import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import {
  FormFieldMultiSelect,
  type FormFieldMultiSelectProps
} from '@/components/form/FormFieldMultiSelect';

export interface FormMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<FormFieldMultiSelectProps, 'name' | 'value' | 'onChange' | 'error'> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: string[];
}

/**
 * React Hook Form adapter for FormFieldMultiSelect
 * Automatically handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormMultiSelect
 *   control={control}
 *   name="tags"
 *   label="Tags"
 *   items={[
 *     { value: 'react', label: 'React' },
 *     { value: 'typescript', label: 'TypeScript' }
 *   ]}
 * />
 * ```
 */
export function FormMultiSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ control, name, defaultValue, ...fieldProps }: FormMultiSelectProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => (
        <FormFieldMultiSelect
          {...fieldProps}
          value={field.value || []}
          onChange={field.onChange}
          error={fieldErrorMessage(fieldState.error)}
        />
      )}
    />
  );
}

FormMultiSelect.displayName = 'FormMultiSelect';
